/* eslint complexity: ["error", { max: 7 }] -- applies to entire file */
/* eslint max-lines-per-function: ["error", { max: 200 }] -- applies to entire file */
import { useCallback } from "react";

import isNil from "lodash/isNil";

import {
  DocumentCategory,
  DocumentFileSource,
  DocumentVoidReasonCode,
} from "@/constants/Documents";
import type { TaskUploadFileV2Params } from "@/constants/Tasks";
import useAppMonitor from "@/services/AppMonitor";
import {
  useLazyGetUploadInsuranceCardUrlQuery,
  useLazyGetUploadInsuranceCardUrlV2Query,
  useUploadFileMutation,
  useVoidPatientDocumentMutation,
} from "@/store/api/documents";
import { useUpdateTaskMutation } from "@/store/api/tasks";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { isNotNilNotEmpty } from "@/utils/shared.utils";

interface MinimalPatientDocument {
  documentExternalId: string;
  patientExternalId: string;
}

interface UploadFilesProps {
  backParams: InsuranceCardUploadParams;
  frontParams: InsuranceCardUploadParams;
  selectedUserId: string;
  taskCacheKey: {
    linkedPatients: LinkedPatientProfile[];
  };
  taskId: string;
}

interface UploadFilesV2Props {
  backParams: TaskUploadFileV2Params;
  frontParams: TaskUploadFileV2Params;
  selectedUserId: string;
  setDocuments: (documents: MinimalPatientDocument[]) => void;
  taskCacheKey: {
    linkedPatients: LinkedPatientProfile[];
  };
  taskId: string;
}

const useTasksFileUpload = () => {
  const { captureException } = useAppMonitor();
  const { externalId: appUserId, isPatient } = useAppUserSelector();
  const [
    getUploadUrl,
    { error: getUploadUrlError, isLoading: isGetUploadUrlLoading },
  ] = useLazyGetUploadInsuranceCardUrlQuery();
  const [
    getUploadUrlV2,
    { error: getUploadUrlV2Error, isLoading: isGetUploadUrlV2Loading },
  ] = useLazyGetUploadInsuranceCardUrlV2Query();
  const [
    uploadFileMutation,
    { error: uploadFileError, isLoading: isUploadFileLoading },
  ] = useUploadFileMutation();
  const [
    updateTask,
    { error: updateTaskError, isLoading: isUpdateTaskLoading },
  ] = useUpdateTaskMutation();
  const [voidPatientDocument] = useVoidPatientDocumentMutation();
  const isLoading =
    isGetUploadUrlLoading ||
    isGetUploadUrlV2Loading ||
    isUploadFileLoading ||
    isUpdateTaskLoading;
  const error =
    getUploadUrlError ||
    getUploadUrlV2Error ||
    uploadFileError ||
    updateTaskError;

  const uploadFile = async ({
    file,
    fileName,
    linkedNoteId,
  }: InsuranceCardUploadParams): Promise<void> => {
    try {
      // Step 1: Get the upload URL
      const uploadUrlResponse = await getUploadUrl({
        fileName,
        linkedNoteId,
      }).unwrap();

      // Step 2: Upload file to the URL
      void uploadFileMutation({
        file,
        uploadUrl: uploadUrlResponse.attachmentUploadUrl,
      }).unwrap();
    } catch (err) {
      captureException(err);
      throw err;
    }
  };

  const uploadFiles = async ({
    backParams,
    frontParams,
    selectedUserId,
    taskCacheKey,
    taskId,
  }: UploadFilesProps) => {
    const { linkedPatients } = taskCacheKey;
    try {
      await uploadFile(frontParams);
      await uploadFile(backParams);
      await updateTask({
        appUserId,
        isPatient,
        linkedPatients,
        selectedUserId,
        taskId,
      });
    } catch (err) {
      captureException(err);
      throw err;
    }
  };

  const uploadFileV2 = useCallback(
    async ({
      documentTags,
      file,
      fileName,
      fileSize,
      patientExternalId,
    }: TaskUploadFileV2Params): Promise<MinimalPatientDocument> => {
      try {
        // Step 1: Get the upload URL
        const response = await getUploadUrlV2({
          documentCategory: DocumentCategory.Task,
          documentTags,
          fileName,
          fileSize,
          fileSource: DocumentFileSource.Task,
          patientExternalId,
        }).unwrap();
        if (
          isNil(response) ||
          isNil(response.data) ||
          !isNotNilNotEmpty(response.data.uploadUrl) ||
          !isNotNilNotEmpty(response.data.documentKey)
        ) {
          throw new Error(
            "GetUploadUrlV2 response missing required documentKey/uploadUrl",
          );
        }
        const {
          data: { documentKey: documentExternalId, uploadUrl },
        } = response;

        // Step 2: Upload file to the URL
        await uploadFileMutation({
          file,
          uploadUrl,
        }).unwrap();

        return {
          documentExternalId,
          patientExternalId,
        };
      } catch (err) {
        captureException(err);
        throw err;
      }
    },
    [captureException, getUploadUrlV2, uploadFileMutation],
  );

  const uploadFilesV2 = useCallback(
    async ({
      backParams,
      frontParams,
      selectedUserId,
      setDocuments,
      taskCacheKey,
      taskId,
    }: UploadFilesV2Props) => {
      const { linkedPatients } = taskCacheKey;
      try {
        const frontDocument = await uploadFileV2(frontParams);
        setDocuments([frontDocument]);

        const backDocument = await uploadFileV2(backParams);
        setDocuments([frontDocument, backDocument]);

        await updateTask({
          appUserId,
          isPatient,
          linkedPatients,
          selectedUserId,
          taskId,
        }).unwrap();
      } catch (err) {
        captureException(err);
        throw err;
      }
    },
    [appUserId, captureException, isPatient, updateTask, uploadFileV2],
  );

  const voidPatientDocuments = useCallback(
    async ({ documents }: { documents: MinimalPatientDocument[] }) => {
      try {
        await Promise.all(
          documents.map((doc) =>
            voidPatientDocument({
              documentExternalId: doc.documentExternalId,
              patientExternalId: doc.patientExternalId,
              voidReasonCode: DocumentVoidReasonCode.Other,
            }).unwrap(),
          ),
        );
      } catch (err) {
        captureException(err);
        throw err;
      }
    },
    [captureException, voidPatientDocument],
  );

  return {
    error,
    isLoading,
    uploadFiles,
    uploadFilesV2,
    voidPatientDocuments,
  };
};

export default useTasksFileUpload;

export type { MinimalPatientDocument, UploadFilesProps, UploadFilesV2Props };
